/*
Copyright (C) 2026 Laboratorio Visao Robotica e Imagem

Departamento de Informatica - Universidade Federal do Parana - VRI/UFPR
*/

import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { StorageContext } from '../contexts/StorageContext';
import SplashPage from './SplashPage';
import { AuthContext } from '../contexts/AuthContext';
import { LayoutContext } from '../contexts/LayoutContext';
import { AlertContext } from '../contexts/AlertContext';
import ErrorPage from './ErrorPage';
import { serialize } from 'object-to-formdata';
import { MapContainer, Marker, TileLayer } from 'react-leaflet';
import imageCompression from 'browser-image-compression';

const styles = `
    .bg-roadkill-base { background-color: #f1ebd8; }
    .bg-roadkill-primary { background-color: #718654; }
    .text-roadkill-dark { color: #333333; }
    .font-barlow { font-family: 'Barlow', sans-serif; }

    .animal-entry {
        background-color: rgba(113, 134, 84, 0.1);
        border: 1px dashed #718654;
        border-radius: 14px;
    }

    .upload-wrapper {
        background-color: #718654;
        color: #ffffff;
        border-radius: 12px;
        border: 2px dashed transparent;
        transition: all 0.2s ease;
        cursor: pointer;
    }

    .upload-wrapper:hover {
        background-color: #5c6e44;
        border-color: rgba(255, 255, 255, 0.4);
    }

    .upload-wrapper.disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    .rk-input {
        background-color: #e8e8e8;
        color: #333333;
        border: none;
        border-radius: 6px;
        padding: 12px;
        width: 100%;
        outline: none;
    }

    .rk-input:focus {
        box-shadow: 0 0 0 3px rgba(113, 134, 84, 0.4);
    }

    .card-rk {
        background-color: #718654;
        color: #ffffff;
        border-radius: 12px;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.12);
    }

    .btn-submit-web {
        background-color: #4a5a3a;
        color: white;
        border: none;
        padding: 18px;
        border-radius: 12px;
        font-size: 18px;
        font-weight: bold;
        cursor: pointer;
        box-shadow: 0 4px 10px rgba(0,0,0,0.12);
        transition: background 0.2s, transform 0.1s;
        text-align: center;
        width: 100%;
    }

    .btn-submit-web:hover:not(:disabled) { 
        background-color: #3d4a30; 
    }
    .btn-submit-web:active:not(:disabled) {
        transform: scale(0.99);
    }
    .btn-submit-web:disabled {
        background-color: #8fa07e;
        cursor: not-allowed;
        opacity: 0.8;
    }

    .consent-wrapper {
        padding: 8px 16px;
        margin-top: 4px;
        margin-bottom: 4px;
        background-color: rgba(113, 134, 84, 0.05);
        border: 1px solid rgba(113, 134, 84, 0.2);
        border-radius: 8px;
    }

    .checkbox-label {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        font-size: 13px;
        line-height: 1.4;
        color: #5c6e44;
        cursor: pointer;
        margin: 0;
    }

    .checkbox-label input[type="checkbox"] {
        accent-color: #718654;
        width: 20px;
        height: 20px;
        flex-shrink: 0;
        margin-top: 2px;
        cursor: pointer;
    }

    .checkbox-label strong {
        display: block;
        margin-bottom: 2px;
        font-size: 14px;
        color: #333333;
    }

    .disclaimer-box {
        border-top: 1px solid rgba(113, 134, 84, 0.3);
        padding-top: 24px;
        display: flex;
        align-items: flex-start;
        gap: 12px;
        color: #5c6e44;
        font-size: 14px;
        line-height: 1.5;
    }

    .icon-info {
        width: 24px;
        height: 24px;
        fill: #5c6e44;
        flex-shrink: 0;
        margin-top: 2px;
    }

    .spinner-splash{
        width: 50px;
        height: 50px;
    }

    .leaflet-container {
        background-color: #718654 !important;
    }

    @media (min-width: 992px) {
      .position-lg-sticky { position: sticky !important; top: 0; }
    }
`;

// Funções para capturar a data e a hora do sistema no formato exigido pelo input
const getCurrentDate = () => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
};

const getCurrentTime = () => {
    const today = new Date();
    return `${String(today.getHours()).padStart(2, '0')}:${String(today.getMinutes()).padStart(2, '0')}`;
};

function RoadkillLocationPicker({ answer, onAnswerChange }) {
    const mapRef = useRef(null);

    const defaultLocation = useCallback(() => {
        navigator.geolocation?.getCurrentPosition((pos) =>
            onAnswerChange({ latitude: pos.coords.latitude, longitude: pos.coords.longitude })
        );
    }, [onAnswerChange]);

    useEffect(() => {
        if (!answer.latitude && !answer.longitude) defaultLocation();
    }, [answer, defaultLocation]);

    return (
        <div className="card-rk p-0 overflow-hidden" style={{ backgroundColor: '#718654', color: '#fff' }}>
            <div className="p-3 d-flex flex-column gap-3">
                <div className="d-flex justify-content-between align-items-center">
                    <label className="fw-bold text-uppercase small m-0">Local da coleta</label>
                    <button
                        type="button"
                        onClick={defaultLocation}
                        className="btn btn-sm d-flex align-items-center gap-1"
                        style={{ backgroundColor: '#f1ebd8', color: '#5c6e44', fontWeight: 'bold' }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z" />
                        </svg>
                        GPS Local
                    </button>
                </div>
                <div className="row g-2">
                    <div className="col-6">
                        <input
                            type="number"
                            disabled
                            className="rk-input"
                            placeholder="Latitude"
                            value={answer.latitude || ''}
                            onChange={(e) => onAnswerChange({ ...answer, latitude: e.target.value })}
                        />
                    </div>
                    <div className="col-6">
                        <input
                            type="number"
                            disabled
                            className="rk-input"
                            placeholder="Longitude"
                            value={answer.longitude || ''}
                            onChange={(e) => onAnswerChange({ ...answer, longitude: e.target.value })}
                        />
                    </div>
                </div>
            </div>

            <div style={{ height: '300px', width: '100%', borderTop: '1px solid rgba(255, 255, 255, 0.2)', backgroundColor: '#718654' }}>
                <MapContainer
                    center={[-14.235, -51.9253]}
                    zoom={6}
                    style={{ height: '100%', width: '100%' }}
                    ref={mapRef}
                    worldCopyJump={true}
                >
                    <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <Marker
                        position={[answer.latitude || -14.235, answer.longitude || -51.9253]}
                        draggable={true}
                        eventHandlers={{
                            moveend: (e) => {
                                const { lat, lng } = e.target.getLatLng();
                                const longitude = (((lng % 360) + 540) % 360) - 180;
                                onAnswerChange({ latitude: lat, longitude });
                                mapRef.current.setView([lat, longitude]);
                            },
                            add: () => {
                                const latitude = answer.latitude || -14.235;
                                const longitude = (((answer.longitude % 360) + 540) % 360) - 180 || -51.9253;
                                mapRef.current.setView([latitude, longitude]);
                                onAnswerChange({ latitude, longitude });
                            },
                        }}
                    />
                </MapContainer>
            </div>
        </div>
    );
}

function DynamicRoadkillPage() {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(undefined);
    const { user } = useContext(AuthContext);
    const [application, setApplication] = useState(undefined);
    const [itemAnswerGroups, setItemAnswerGroups] = useState({});

    const { login } = useContext(AuthContext);

    // Os estados já são iniciados com a data e a hora atuais do sistema
    const [answerDate, setAnswerDate] = useState(getCurrentDate());
    const [answerTime, setAnswerTime] = useState(getCurrentTime());

    const [answerLocation, setAnswerLocation] = useState({ latitude: '', longitude: '' });
    const [imageConsent, setImageConsent] = useState(false);

    const [isCompressing, setIsCompressing] = useState(false);

    const applicationId = 1;
    const { connected, storeLocalApplication, storePendingRequest, localApplications } = useContext(StorageContext);
    const { showAlert } = useContext(AlertContext);
    const navigate = useNavigate();
    const { isDashboard } = useContext(LayoutContext);

    const handleAnswerChange = useCallback((groupToUpdate, itemToUpdate, itemType, updatedAnswer) => {
        setItemAnswerGroups((prev) => {
            const typeKey =
                itemType === 'ITEM'
                    ? 'itemAnswers'
                    : itemType === 'OPTION'
                    ? 'optionAnswers'
                    : itemType === 'TABLE'
                    ? 'tableAnswers'
                    : null;

            if (!typeKey) return prev;
            const currentGroup = prev[groupToUpdate] || { itemAnswers: {}, optionAnswers: {}, tableAnswers: {} };
            const currentTypeAnswers = { ...currentGroup[typeKey] };

            if (Object.keys(updatedAnswer).length === 0) {
                delete currentTypeAnswers[itemToUpdate];
            } else {
                currentTypeAnswers[itemToUpdate] = updatedAnswer;
            }

            return { ...prev, [groupToUpdate]: { ...currentGroup, [typeKey]: currentTypeAnswers } };
        });
    }, []);

    const handleProtocolSubmit = (e) => {
        e.preventDefault();

        if (!imageConsent) {
            showAlert({ headerText: 'Você precisa autorizar o uso das imagens para continuar.' });
            return;
        }

        showAlert({ headerText: 'Aguarde o processamento da resposta.', isClosable: false });

        const applicationAnswer = {
            applicationId: application.id,
            date: new Date(answerDate + 'T' + answerTime + ':00Z'),
            coordinate: answerLocation,
            itemAnswerGroups: [],
        };

        for (const group in itemAnswerGroups) {
            const itemAnswerGroup = { itemAnswers: [], optionAnswers: [], tableAnswers: [] };

            for (const item in itemAnswerGroups[group].itemAnswers) {
                const itemAnswer = itemAnswerGroups[group].itemAnswers[item];

                if (itemAnswer.files && itemAnswer.files.length > 0) {
                    itemAnswerGroup.itemAnswers.push({
                        itemId: item,
                        text: itemAnswer.text || '',
                        files: itemAnswer.files,
                    });
                } else {
                    itemAnswerGroup.itemAnswers.push({
                        itemId: item,
                        text: itemAnswer.text || '',
                    });
                }
            }

            for (const item in itemAnswerGroups[group].optionAnswers) {
                for (const option in itemAnswerGroups[group].optionAnswers[item]) {
                    if (option !== 'group') {
                        itemAnswerGroup.optionAnswers.push({
                            itemId: item,
                            optionId: option,
                            text: itemAnswerGroups[group].optionAnswers[item][option],
                        });
                    }
                }
            }

            applicationAnswer.itemAnswerGroups.push(itemAnswerGroup);
        }

        const formData = serialize(applicationAnswer, { indices: true });

        if (connected) {
            axios
                .post(process.env.REACT_APP_API_URL + `api/applicationAnswer/createApplicationAnswer`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${user.token}` },
                })
                .then(() =>
                    showAlert({
                        headerText: 'Muito obrigado por sua participação no projeto.',
                        onPrimaryBtnClick: () => navigate(isDashboard ? '/dash/applications' : '/applications'),
                    })
                )
                .catch((err) =>
                    showAlert({
                        headerText: 'Erro ao submeter.',
                        bodyText: err.response?.data?.message || err.message,
                    })
                );
        } else {
            storePendingRequest({
                id: applicationId,
                userId: user.id,
                title: 'Resposta da aplicação ' + applicationId,
                url: process.env.REACT_APP_API_URL + `api/applicationAnswer/createApplicationAnswer`,
                data: formData,
                config: { headers: { 'Content-Type': 'multipart/form-data' } },
            });
            showAlert({ headerText: 'Você está offline. A resposta será armazenada localmente.' });
        }
    };

// Roda UMA vez ao montar o componente
useEffect(() => {
    axios
        .get(process.env.REACT_APP_API_URL + 'api/auth/passwordlessSignIn', {
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((response) => {
            if (response.data.data.token) {
                login(
                    response.data.data.id,
                    'Visitante',
                    response.data.data.role,
                    response.data.data.token,
                    new Date(new Date().getTime() + response.data.data.expiresIn),
                    false,
                    response.data.data.institutionId
                );
            } else throw new Error('Authentication failed!');
        })
        .catch(() => showAlert({ headerText: 'Falha de autenticação.' }));
}, []); // ← sem dependências, roda só uma vez!

// Roda quando o user ou os dados locais mudam
useEffect(() => {
    if (localApplications !== undefined && application === undefined) {
        const localApp = localApplications.find((app) => app.id === parseInt(applicationId));
        if (localApp !== undefined && connected === false) {
            setApplication(localApp);
            setIsLoading(false);
        } else if (user.id && user.token) {
            axios
                .get(process.env.REACT_APP_API_URL + `api/application/getApplicationWithProtocol/${applicationId}`, {
                    headers: { Authorization: `Bearer ${user.token}` },
                })
                .then((res) => {
                    setApplication(res.data.data);
                    storeLocalApplication(res.data.data);
                    setIsLoading(false);
                })
                .catch((err) => setError({ text: 'Erro ao carregar', description: err.message }));
        }
    }
}, [applicationId, user, localApplications, application, connected, storeLocalApplication]);

    if (error) return <ErrorPage text={error.text} description={error.description} />;
    if (isLoading)
        return (
            <div className="d-flex justify-content-center align-items-center vh-100 bg-roadkill-base">
                <div className="spinner-border text-secondary spinner-splash" role="status">
                    <span className="sr-only"></span>
                </div>

                <style>{styles}</style>
            </div>
        );

    const currentPageItems = application.protocol.pages[0]?.itemGroups[0]?.items || [];
    const imageItems = currentPageItems.filter((item) => item.type === 'UPLOAD');
    const metadataItems = currentPageItems.filter((item) => item.type !== 'UPLOAD');
    const groupId = application.protocol.pages[0]?.itemGroups[0]?.id;

    return (
        <div className="d-flex flex-column flex-grow-1 w-100 min-vh-100 font-barlow">
            <div className="d-flex flex-column flex-grow-1 overflow-auto bg-roadkill-base p-0 text-roadkill-dark py-5">
                <div className="row d-flex justify-content-center h-100 p-0 m-0 my-4">
                    <div className="col-12 col-md-9 px-4 px-lg-5">
                        <form onSubmit={handleProtocolSubmit} className="row g-4 align-items-start">
                            <div className="col-12 order-0 text-center mb-2">
                                <h2 className="fw-bold text-uppercase" style={{ color: '#5c6e44' }}>
                                    Nova Observação de Fauna Atropelada
                                </h2>
                                <p style={{ color: '#5c6e44', opacity: 0.85, fontSize: '1.1rem' }}>
                                    Preencha os dados do evento e anexe as imagens para análise.
                                </p>
                            </div>

                            <div className="col-12 col-md-6 order-1 d-flex flex-column gap-3">
                                {imageItems.map((item) => {
                                    const currentFiles = itemAnswerGroups[groupId]?.itemAnswers?.[item.id]?.files || [];

                                    return (
                                        <div key={item.id} className="animal-entry p-3 d-flex flex-column gap-3">
                                            <label
                                                className={`upload-wrapper p-4 d-flex flex-column align-items-center justify-content-center text-center ${
                                                    isCompressing ? 'disabled' : ''
                                                }`}
                                            >
                                                {isCompressing ? (
                                                    <div className="spinner-border text-white mb-2" role="status">
                                                        <span className="visually-hidden">Processando...</span>
                                                    </div>
                                                ) : (
                                                    <svg width="50" height="50" fill="currentColor" className="mb-2" viewBox="0 0 24 24">
                                                        <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5.04-6.71l-2.75 3.54-1.96-2.36L6.5 17h11l-3.54-4.71z" />
                                                    </svg>
                                                )}

                                                <span className="fw-bold">
                                                    {isCompressing
                                                        ? 'Processando imagens...'
                                                        : currentFiles.length > 0
                                                        ? 'Adicionar mais imagens'
                                                        : item.text || 'Anexar imagens da carcaça'}
                                                </span>
                                                <span className="small opacity-75">
                                                    {isCompressing ? 'Aguarde um momento' : 'Clique para selecionar do dispositivo'}
                                                </span>

                                                <input
                                                    type="file"
                                                    className="d-none"
                                                    accept="image/*"
                                                    multiple
                                                    required={currentFiles.length === 0}
                                                    disabled={isCompressing}
                                                    onChange={async (e) => {
                                                        const inputElement = e.target;
                                                        if (!inputElement.files || inputElement.files.length === 0) return;

                                                        setIsCompressing(true);
                                                        try {
                                                            const rawFiles = Array.from(inputElement.files);
                                                            const options = { maxSizeMB: 2, useWebWorker: true };
                                                            const processedFiles = [];

                                                            for (const file of rawFiles) {
                                                                try {
                                                                    const compressedBlob = await imageCompression(file, options);
                                                                    const extension = file.name.split('.').pop() || 'jpg';
                                                                    const compressedFile = new File(
                                                                        [compressedBlob],
                                                                        `compressed_${Date.now()}.${extension}`,
                                                                        { type: compressedBlob.type }
                                                                    );
                                                                    processedFiles.push(compressedFile);
                                                                } catch (err) {
                                                                    console.error('Falha ao comprimir imagem.', err);
                                                                    processedFiles.push(file);
                                                                }
                                                            }

                                                            const newFilesArray = [...currentFiles, ...processedFiles];

                                                            handleAnswerChange(groupId, item.id, 'ITEM', {
                                                                files: newFilesArray,
                                                                text: itemAnswerGroups[groupId]?.itemAnswers?.[item.id]?.text || '',
                                                            });
                                                        } finally {
                                                            setIsCompressing(false);
                                                            inputElement.value = '';
                                                        }
                                                    }}
                                                />
                                            </label>

                                            {currentFiles.length > 0 && (
                                                <div className="d-flex flex-wrap gap-2 mt-1">
                                                    {currentFiles.map((file, index) => (
                                                        <div
                                                            key={index}
                                                            className="position-relative bg-white rounded shadow-sm"
                                                            style={{
                                                                width: '110px',
                                                                height: '110px',
                                                                border: '1px solid rgba(113, 134, 84, 0.4)',
                                                            }}
                                                        >
                                                            <img
                                                                className="img-fluid object-fit-cover w-100 h-100 rounded"
                                                                src={URL.createObjectURL(file)}
                                                                alt={`Pré-visualização ${index + 1}`}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger position-absolute top-0 end-0 p-0 d-flex align-items-center justify-content-center rounded-circle shadow"
                                                                style={{
                                                                    width: '20px',
                                                                    height: '20px',
                                                                    transform: 'translate(30%, -30%)',
                                                                    zIndex: 10,
                                                                }}
                                                                disabled={isCompressing}
                                                                onClick={() => {
                                                                    const updatedFiles = currentFiles.filter((_, i) => i !== index);
                                                                    handleAnswerChange(groupId, item.id, 'ITEM', {
                                                                        files: updatedFiles,
                                                                        text: itemAnswerGroups[groupId]?.itemAnswers?.[item.id]?.text || '',
                                                                    });
                                                                }}
                                                            >
                                                                <svg width="10" height="10" fill="currentColor" viewBox="0 0 16 16">
                                                                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}

                                {metadataItems.map((item) => {
                                    if (item.type === 'TEXTBOX' || item.type === 'TEXT') {
                                        return (
                                            <div key={item.id} className="card-rk p-3 d-flex flex-column gap-1">
                                                <label className="fw-bold text-uppercase small mb-1">{item.text}</label>
                                                <input
                                                    type="text"
                                                    className="rk-input"
                                                    placeholder="Ex: Graxaim-do-campo, Tamanduá-mirim..."
                                                    onChange={(e) => {
                                                        handleAnswerChange(groupId, item.id, 'ITEM', {
                                                            text: e.target.value,
                                                        });
                                                    }}
                                                />
                                            </div>
                                        );
                                    }

                                    if (item.type === 'RADIO') {
                                        return (
                                            <div key={item.id} className="card-rk p-3 d-flex flex-column gap-1">
                                                <label className="fw-bold text-uppercase small mb-2">{item.text}</label>
                                                <div className="d-flex flex-wrap gap-4">
                                                    {item.itemOptions?.map((opt) => (
                                                        <label
                                                            key={opt.id}
                                                            className="d-flex align-items-center gap-2"
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <input
                                                                type="radio"
                                                                name={`radio-${item.id}`}
                                                                value={opt.id}
                                                                style={{ accentColor: '#f1ebd8', width: '18px', height: '18px' }}
                                                                onChange={() => {
                                                                    handleAnswerChange(groupId, item.id, 'OPTION', {
                                                                        [opt.id]: opt.text,
                                                                    });
                                                                }}
                                                                required
                                                            />
                                                            {opt.text}
                                                        </label>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    }

                                    if (item.type === 'SELECT') {
                                        return (
                                            <div key={item.id} className="card-rk p-3 d-flex flex-column gap-1">
                                                <label className="fw-bold text-uppercase small mb-1">{item.text}</label>
                                                <select
                                                    className="rk-input"
                                                    required
                                                    defaultValue=""
                                                    onChange={(e) => {
                                                        const selectedOpt = item.itemOptions.find(
                                                            (o) => o.id.toString() === e.target.value
                                                        );
                                                        handleAnswerChange(groupId, item.id, 'OPTION', {
                                                            [selectedOpt.id]: selectedOpt.text,
                                                        });
                                                    }}
                                                >
                                                    <option value="" disabled>
                                                        Selecione uma opção...
                                                    </option>
                                                    {item.itemOptions?.map((opt) => (
                                                        <option key={opt.id} value={opt.id}>
                                                            {opt.text}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>

                            <div className="col-12 col-md-6 order-2 d-flex flex-column gap-3">
                                <div className="card-rk p-3 d-flex align-items-center gap-3">
                                    <div className="flex-grow-1">
                                        <label className="fw-bold text-uppercase small mb-1">Data da coleta</label>
                                        <input
                                            type="date"
                                            className="rk-input"
                                            required
                                            value={answerDate}
                                            onChange={(e) => setAnswerDate(e.target.value)}
                                        />
                                    </div>
                                </div>

                                <div className="card-rk p-3 d-flex align-items-center gap-3">
                                    <div className="flex-grow-1">
                                        <label className="fw-bold text-uppercase small mb-1">Hora da coleta</label>
                                        <input
                                            type="time"
                                            className="rk-input"
                                            required
                                            value={answerTime}
                                            onChange={(e) => setAnswerTime(e.target.value)}
                                        />
                                    </div>
                                </div>

                                {application.keepLocation && (
                                    <RoadkillLocationPicker answer={answerLocation} onAnswerChange={setAnswerLocation} />
                                )}
                            </div>

                            <div className="col-12 order-3 mt-4">
                                <div className="d-flex flex-column align-items-center gap-4">
                                    <div className="consent-wrapper w-100" style={{ maxWidth: '800px' }}>
                                        <label className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                name="consentimento"
                                                required
                                                checked={imageConsent}
                                                onChange={(e) => setImageConsent(e.target.checked)}
                                            />
                                            <span>
                                                <strong>Autorização de Uso de Imagem</strong>
                                                Concordo com o envio destas imagens para compor o banco de dados do projeto, auxiliando na
                                                união entre ecologia de estradas e visão computacional para a classificação automática da
                                                fauna atropelada.
                                            </span>
                                        </label>
                                    </div>

                                    <div className="w-100" style={{ maxWidth: '800px' }}>
                                        <button type="submit" className="btn-submit-web py-3" disabled={!imageConsent || isCompressing}>
                                            Enviar Registro Completo
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 order-4 mt-4 mb-4">
                                <div className="disclaimer-box">
                                    <svg className="icon-info" viewBox="0 0 24 24">
                                        <path d="M11 7h2v2h-2zm0 4h2v6h-2zm1-9C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                                    </svg>
                                    <div className="disclaimer-text">
                                        <p className="m-0">
                                            <strong>Apoio à Ciência:</strong> Agradecemos a sua contribuição! As imagens e metadados
                                            submetidos serão utilizados exclusivamente para pesquisas científicas no Brasil. No futuro,
                                            esses registos integrarão bancos de dados públicos para o treino de modelos de Inteligência
                                            Artificial e auxiliarão na formulação de medidas mitigadoras para a conservação da
                                            biodiversidade nas rodovias.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <style>{styles}</style>
        </div>
    );
}

export default DynamicRoadkillPage;

